<template>
  <div class="notification-editor">
    <div class="modal fade show d-block" tabindex="-1" role="dialog" v-if="show">
      <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-bell me-2"></i>
              {{ $t('messages.notificationSettings') }}
            </h5>
            <button type="button" class="btn-close" @click="cancel" aria-label="Close"></button>
          </div>
          
          <div class="modal-body">
            <!-- Loading State -->
            <div v-if="isLoading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">{{ $t('messages.loadingAddressBook') }}</span>
              </div>
            </div>
            
            <!-- Error State -->
            <div v-else-if="errorMessage" class="alert alert-danger" role="alert">
              <i class="bi bi-exclamation-triangle-fill me-2"></i>
              {{ errorMessage }}
            </div>
            
            <!-- Main Content -->
            <div v-else>
              <!-- Notification Types -->
              <div v-for="notificationType in notificationTypes" :key="notificationType.type" class="mb-4">
                <div class="card">
                  <div class="card-header">
                    <div class="form-check">
                      <input 
                        class="form-check-input" 
                        type="checkbox" 
                        :id="'notify-' + notificationType.type"
                        v-model="enabledNotifications[notificationType.type]"
                        @change="toggleNotificationType(notificationType.type)"
                      >
                      <label class="form-check-label" :for="'notify-' + notificationType.type">
                        <strong>{{ notificationType.label }}</strong>
                      </label>
                    </div>
                    <div class="text-muted small mt-1">{{ notificationType.description }}</div>
                  </div>
                  
                  <div v-if="enabledNotifications[notificationType.type]" class="card-body">
                    <!-- Recipients Selection -->
                    <div class="mb-3">
                      <label class="form-label">{{ $t('messages.recipients') }}</label>
                      <div class="list-group">
                        <div 
                          v-for="entry in addressBookEntries" 
                          :key="entry.id"
                          class="list-group-item"
                        >
                          <div class="form-check">
                            <input 
                              class="form-check-input" 
                              type="checkbox" 
                              :id="`${notificationType.type}-${entry.id}`"
                              :checked="isRecipientSelected(notificationType.type, entry.id)"
                              @change="toggleRecipient(notificationType.type, entry.id)"
                            >
                            <label class="form-check-label w-100" :for="`${notificationType.type}-${entry.id}`">
                              <div class="d-flex justify-content-between align-items-start">
                                <div>
                                  <strong>{{ entry.name }}</strong>
                                  <div class="text-muted small">{{ entry.email }}</div>
                                </div>
                                
                                <!-- Result Filters for JOB_RESULTS_EMAIL -->
                                <div v-if="notificationType.type === 'JOB_RESULTS_EMAIL' && isRecipientSelected(notificationType.type, entry.id)">
                                  <button 
                                    type="button" 
                                    class="btn btn-sm btn-outline-secondary"
                                    @click="editFilters(notificationType.type, entry.id)"
                                  >
                                    <i class="bi bi-funnel"></i>
                                    {{ getFilterCount(notificationType.type, entry.id) }}
                                  </button>
                                </div>
                              </div>
                            </label>
                          </div>
                        </div>
                      </div>
                      
                      <div v-if="addressBookEntries.length === 0" class="text-muted text-center py-3">
                        {{ $t('messages.noAddressBookEntries') }}
                        <!--
                        <button type="button" class="btn btn-link btn-sm" @click="openAddressBook">
                          {{ $t('messages.manageAddressBook') }}
                        </button>
                        -->
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="cancel">
              {{ $t('messages.cancel') }}
            </button>
            <button type="button" class="btn btn-primary" @click="save" :disabled="isProcessing">
              <span v-if="isProcessing" class="spinner-border spinner-border-sm me-2" role="status"></span>
              {{ $t('messages.save') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Filter Editor Modal -->
    <div v-if="showFilterEditor" class="modal fade show d-block" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-funnel me-2"></i>
              {{ $t('messages.resultFilters') }}
            </h5>
            <button type="button" class="btn-close" @click="closeFilterEditor" aria-label="Close"></button>
          </div>
          
          <div class="modal-body">
            <p class="text-muted">{{ $t('messages.filterInstructions') }}</p>
            
            <div v-for="(filter, index) in currentFilters" :key="index" class="mb-2">
              <div class="input-group">
                <input 
                  v-model="currentFilters[index]" 
                  type="text" 
                  class="form-control" 
                  :placeholder="$t('messages.filterPlaceholder')"
                >
                <button 
                  type="button" 
                  class="btn btn-outline-danger"
                  @click="removeFilter(index)"
                >
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </div>
            
            <button 
              type="button" 
              class="btn btn-outline-primary btn-sm mt-2"
              @click="addFilter"
            >
              <i class="bi bi-plus-circle me-1"></i>
              {{ $t('messages.addFilter') }}
            </button>
            
            <div class="mt-3">
              <div class="form-text">
                {{ $t('messages.filterExamples') }}
                <ul class="mb-0">
                  <li><code>location:New York Office</code></li>
                  <li><code>element:temp,humidity</code></li>
                  <li><code>date_range:2024-01-15,2024-01-31</code></li>
                </ul>
              </div>
            </div>
          </div>
          
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeFilterEditor">
              {{ $t('messages.cancel') }}
            </button>
            <button type="button" class="btn btn-primary" @click="saveFilters">
              {{ $t('messages.apply') }}
            </button>
          </div>
        </div>
      </div>
    </div>
        <!-- Filter Editor Modal -->
    <div v-if="showAddressBook" class="modal fade show d-block" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-funnel me-2"></i>
              {{ $t('messages.addressbook') }}
            </h5>
            <button type="button" class="btn-close" @click="showAddressBook = false" aria-label="Close"></button>
          </div>
          
          <div class="modal-body">
            <AddressBookEditor
              @close="showAddressBook = false"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import translations from '@/translations/notificationeditor.js';
import AddressBookEditor from './AddressBookEditor.vue';
export default {
  i18n: {
      messages: translations,
  },
  name: 'NotificationEditor',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    notificationConfigs: {
      type: Array,
      default: () => []
    }
  },
  emits: ['save', 'cancel', 'openAddressBook'],
  data() {
    return {
      isLoading: false,
      isProcessing: false,
      errorMessage: '',
      addressBookEntries: [],
      localConfigs: [],
      enabledNotifications: {},
      showFilterEditor: false,
      showAddressBook: false,
      currentEditingType: null,
      currentEditingRecipient: null,
      currentFilters: [],
      notificationTypes: [
        {
          type: 'JOB_COMPLETE_EMAIL',
          label: this.$t('messages.jobCompleteEmail'),
          description: this.$t('messages.jobCompleteEmailDesc')
        },
        {
          type: 'JOB_ERROR_EMAIL',
          label: this.$t('messages.jobErrorEmail'),
          description: this.$t('messages.jobErrorEmailDesc')
        },
        {
          type: 'JOB_RESULTS_EMAIL',
          label: this.$t('messages.jobResultsEmail'),
          description: this.$t('messages.jobResultsEmailDesc')
        }
      ]
    };
  },
  computed: {
    vcCommunicator() {
      return window.vcCommunicator || null;
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.loadAddressBook();
        this.initializeConfigs();
      }
    },
    notificationConfigs: {
      handler(newVal) {
        if (this.show) {
          this.initializeConfigs();
        }
      },
      deep: true
    }
  },
  methods: {
    initializeConfigs() {
      // Deep clone the input configs
      this.localConfigs = JSON.parse(JSON.stringify(this.notificationConfigs || []));
      
      // Initialize enabled state
      this.enabledNotifications = {};
      this.notificationTypes.forEach(type => {
        const config = this.localConfigs.find(c => c.notificationType === type.type);
        this.enabledNotifications[type.type] = !!config;
      });
    },
    
    async loadAddressBook() {
      this.isLoading = true;
      this.errorMessage = '';
      
      try {
        if (this.vcCommunicator) {
          const response = await this.vcCommunicator.retrieveData(
            this.vcCommunicator.createUrl('addressbook/list'),
            {}
          );
          
          if (response.success || !response.errorCode) {
            this.addressBookEntries = response.entries || [];
          } else {
            throw new Error(response.message || this.$t('messages.failedToLoadAddressBook'));
          }
        }
      } catch (error) {
        console.error('Failed to load address book:', error);
        this.errorMessage = error.message || this.$t('messages.failedToLoadAddressBook');
      } finally {
        this.isLoading = false;
      }
    },
    
    toggleNotificationType(type) {
      if (this.enabledNotifications[type]) {
        // Add config if it doesn't exist
        if (!this.localConfigs.find(c => c.notificationType === type)) {
          this.localConfigs.push({
            notificationType: type,
            recipients: []
          });
        }
      } else {
        // Remove config
        this.localConfigs = this.localConfigs.filter(c => c.notificationType !== type);
      }
    },
    
    isRecipientSelected(type, entryId) {
      const config = this.localConfigs.find(c => c.notificationType === type);
      if (!config) return false;
      return config.recipients.some(r => r.addressBookEntryId === entryId);
    },
    
    toggleRecipient(type, entryId) {
      const config = this.localConfigs.find(c => c.notificationType === type);
      if (!config) return;
      
      const recipientIndex = config.recipients.findIndex(r => r.addressBookEntryId === entryId);
      
      if (recipientIndex === -1) {
        // Add recipient
        const recipient = {
          addressBookEntryId: entryId
        };
        if (type === 'JOB_RESULTS_EMAIL') {
          recipient.resultFilters = [];
        }
        config.recipients.push(recipient);
      } else {
        // Remove recipient
        config.recipients.splice(recipientIndex, 1);
      }
    },
    
    getFilterCount(type, entryId) {
      const config = this.localConfigs.find(c => c.notificationType === type);
      if (!config) return this.$t('messages.noFilters');
      
      const recipient = config.recipients.find(r => r.addressBookEntryId === entryId);
      if (!recipient || !recipient.resultFilters || recipient.resultFilters.length === 0) {
        return this.$t('messages.noFilters');
      }
      
      return recipient.resultFilters.length === 1 
        ? this.$t('messages.oneFilter') 
        : this.$t('messages.multipleFilters', { count: recipient.resultFilters.length });
    },
    
    editFilters(type, entryId) {
      const config = this.localConfigs.find(c => c.notificationType === type);
      if (!config) return;
      
      const recipient = config.recipients.find(r => r.addressBookEntryId === entryId);
      if (!recipient) return;
      
      this.currentEditingType = type;
      this.currentEditingRecipient = entryId;
      this.currentFilters = [...(recipient.resultFilters || [])];
      this.showFilterEditor = true;
    },
    
    addFilter() {
      this.currentFilters.push('');
    },
    
    removeFilter(index) {
      this.currentFilters.splice(index, 1);
    },
    
    saveFilters() {
      const config = this.localConfigs.find(c => c.notificationType === this.currentEditingType);
      if (!config) return;
      
      const recipient = config.recipients.find(r => r.addressBookEntryId === this.currentEditingRecipient);
      if (!recipient) return;
      
      // Filter out empty strings
      recipient.resultFilters = this.currentFilters.filter(f => f.trim());
      
      this.closeFilterEditor();
    },
    
    closeFilterEditor() {
      this.showFilterEditor = false;
      this.currentEditingType = null;
      this.currentEditingRecipient = null;
      this.currentFilters = [];
    },
    
    openAddressBook() {
      this.showAddressBook = true;
    },
    
    cancel() {
      this.$emit('cancel');
    },
    
    save() {
      // Filter out configs with no recipients
      const validConfigs = this.localConfigs.filter(c => c.recipients && c.recipients.length > 0);
      this.$emit('save', validConfigs);
    }
  },
  components: {
    AddressBookEditor
  }
};
</script>

<style scoped>
.notification-editor .list-group-item {
  transition: background-color 0.2s ease;
}

.notification-editor .list-group-item:hover {
  background-color: var(--bs-gray-100);
}

.notification-editor code {
  background-color: var(--bs-gray-200);
  padding: 2px 4px;
  border-radius: 3px;
}
</style>